// frontend/src/pages/DataWorkspacePage/ConnectionsSidebar.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../api/index.js';
import { useNotification } from '../../context/NotificationContext.jsx';
import { DynamicIcon } from '../../components/icons.jsx';
import AddConnectionModal from './AddConnectionModal.jsx';

/**
 * Left sidebar — shows all active DB connections, and expanding each
 * one reveals its schemas / tables (fetched once from /schema endpoint).
 */

const ENGINE_META = {
    postgresql: { icon: 'Database',    color: 'text-blue-400',   label: 'PostgreSQL' },
    mysql:      { icon: 'Database',    color: 'text-orange-400', label: 'MySQL'      },
    mssql:      { icon: 'DatabaseZap', color: 'text-cyan-400',   label: 'MSSQL'      },
};

const ConnectionsSidebar = ({ token, onSelectTable, activeSelection }) => {
    const { addNotification } = useNotification();
    const [connections, setConnections]   = useState([]);
    const [schemas, setSchemas]           = useState({});         // { [connId]: { tables: [] } }
    const [expanded, setExpanded]         = useState({});         // { [connId]: bool }
    const [loadingSchema, setLoadingSchema] = useState({});       // { [connId]: bool }
    const [showAddModal, setShowAddModal] = useState(false);
    const [search, setSearch]             = useState('');

    // -----------------------------------------------------------------------
    // Load connections
    // -----------------------------------------------------------------------
    const loadConnections = useCallback(async () => {
        try {
            const data = await apiFetch('/data-workspace/connections', token);
            setConnections(data || []);
        } catch (err) {
            addNotification(`Failed to load connections: ${err.message}`, 'error');
        }
    }, [token, addNotification]);

    useEffect(() => { loadConnections(); }, [loadConnections]);

    // -----------------------------------------------------------------------
    // Load schema for a connection (lazy)
    // -----------------------------------------------------------------------
    const loadSchema = useCallback(async (connId) => {
        if (schemas[connId] || loadingSchema[connId]) return;
        setLoadingSchema(prev => ({ ...prev, [connId]: true }));
        try {
            const data = await apiFetch(`/data-workspace/connections/${connId}/schema`, token);
            setSchemas(prev => ({ ...prev, [connId]: data }));
        } catch (err) {
            addNotification(`Schema load failed: ${err.message}`, 'error');
        } finally {
            setLoadingSchema(prev => ({ ...prev, [connId]: false }));
        }
    }, [token, schemas, loadingSchema, addNotification]);

    const toggleConn = (conn) => {
        const next = !expanded[conn.id];
        setExpanded(prev => ({ ...prev, [conn.id]: next }));
        if (next) loadSchema(conn.id);
    };

    // -----------------------------------------------------------------------
    // Test connection
    // -----------------------------------------------------------------------
    const testConnection = async (e, connId) => {
        e.stopPropagation();
        try {
            const res = await apiFetch(`/data-workspace/connections/${connId}/test`, token, { method: 'POST' });
            if (res.success) {
                addNotification('Connection healthy ✓', 'success');
            } else {
                addNotification(`Connection failed: ${res.message}`, 'error');
            }
            loadConnections(); // refresh last_test_ok badge
        } catch (err) {
            addNotification(`Test failed: ${err.message}`, 'error');
        }
    };

    // -----------------------------------------------------------------------
    // Filter tables by search
    // -----------------------------------------------------------------------
    const filterTables = (tables) => {
        if (!search) return tables;
        return tables.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));
    };

    return (
        <div className="w-64 shrink-0 flex flex-col bg-[#0d1526] border-r border-white/10 overflow-hidden">
            {/* Header */}
            <div className="px-4 pt-4 pb-3 border-b border-white/10 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <DynamicIcon name="ServerCog" className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-semibold text-slate-200">Connections</span>
                    </div>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-blue-600/20 text-blue-400 border border-blue-500/30 hover:bg-blue-600/40 transition-colors"
                    >
                        <DynamicIcon name="Plus" className="w-3 h-3" />
                        New
                    </button>
                </div>
                {/* Search */}
                <div className="relative">
                    <DynamicIcon name="Search" className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
                    <input
                        type="text"
                        placeholder="Search tables..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-7 pr-3 py-1.5 rounded-md text-xs bg-white/5 border border-white/10 text-slate-300 placeholder-slate-600 focus:outline-none focus:border-blue-500/50"
                    />
                </div>
            </div>

            {/* Connection List */}
            <div className="flex-1 overflow-y-auto py-2 space-y-0.5 px-2">
                {connections.length === 0 && (
                    <div className="p-4 text-center text-xs text-slate-600 italic">
                        No connections yet.<br />Click "New" to add one.
                    </div>
                )}
                {connections.map(conn => {
                    const meta    = ENGINE_META[conn.engine] || ENGINE_META.postgresql;
                    const isOpen  = expanded[conn.id];
                    const schema  = schemas[conn.id];
                    const loading = loadingSchema[conn.id];
                    const tables  = schema ? filterTables(schema.tables) : [];

                    return (
                        <div key={conn.id}>
                            {/* Connection row */}
                            <button
                                onClick={() => toggleConn(conn)}
                                className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5 transition-colors group text-left"
                            >
                                <DynamicIcon name={isOpen ? 'ChevronDown' : 'ChevronRight'} className="w-3 h-3 text-slate-600 shrink-0" />
                                <DynamicIcon name={meta.icon} className={`w-4 h-4 ${meta.color} shrink-0`} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-slate-300 truncate">{conn.name}</p>
                                    <p className="text-[10px] text-slate-600">{meta.label}</p>
                                </div>
                                {/* Health dot */}
                                <span
                                    className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                                        conn.last_test_ok === true  ? 'bg-emerald-400' :
                                        conn.last_test_ok === false ? 'bg-red-400' :
                                        'bg-slate-600'
                                    }`}
                                    title={conn.last_test_ok === true ? 'Connected' : conn.last_test_ok === false ? 'Failed' : 'Untested'}
                                />
                                <button
                                    onClick={(e) => testConnection(e, conn.id)}
                                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-blue-400 text-slate-600 transition-all"
                                    title="Test connection"
                                >
                                    <DynamicIcon name="Zap" className="w-3 h-3" />
                                </button>
                            </button>

                            {/* Tables */}
                            {isOpen && (
                                <div className="ml-6 mt-0.5 space-y-0.5 mb-1">
                                    {loading && (
                                        <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-slate-500">
                                            <DynamicIcon name="Loader2" className="w-3 h-3 animate-spin" />
                                            Loading schema...
                                        </div>
                                    )}
                                    {!loading && tables.map(tbl => {
                                        const isActive = activeSelection?.connectionId === conn.id && activeSelection?.tableName === tbl.name;
                                        return (
                                            <button
                                                key={tbl.name}
                                                onClick={() => onSelectTable({
                                                    connectionId:   conn.id,
                                                    connectionName: conn.name,
                                                    tableName:      tbl.name,
                                                    schemaName:     null,
                                                    tableType:      tbl.type,
                                                })}
                                                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors ${
                                                    isActive
                                                        ? 'bg-blue-600/30 text-blue-300 border border-blue-500/40'
                                                        : 'hover:bg-white/5 text-slate-400 hover:text-slate-200'
                                                }`}
                                            >
                                                <DynamicIcon
                                                    name={tbl.type === 'view' ? 'Eye' : 'Table'}
                                                    className={`w-3 h-3 shrink-0 ${isActive ? 'text-blue-400' : 'text-slate-600'}`}
                                                />
                                                <span className="text-xs truncate">{tbl.name}</span>
                                                {tbl.type === 'view' && (
                                                    <span className="ml-auto text-[9px] px-1 py-0.5 rounded bg-purple-900/30 text-purple-400 border border-purple-800/30">VIEW</span>
                                                )}
                                            </button>
                                        );
                                    })}
                                    {!loading && tables.length === 0 && (
                                        <p className="px-2 py-1.5 text-[10px] text-slate-600 italic">No tables found</p>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Add Connection Modal */}
            {showAddModal && (
                <AddConnectionModal
                    token={token}
                    onClose={() => setShowAddModal(false)}
                    onCreated={() => { loadConnections(); setShowAddModal(false); }}
                />
            )}
        </div>
    );
};

export default ConnectionsSidebar;
